import { useCallback, useMemo } from 'react';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { WindowWidthContext } from '@minecraft.utils';
import { useTranslation } from '@blocs.i18n';
import { FileTypeWithMeta } from './types';
import { NameCell, FileTypeCell, MobileCell, CheckboxCell, ThumbnailCell } from './PreviewCellRenderers';

type UseUploadMediaPreviewDefsParams = {
  currentMediaType: MediaTypeCodeEnum;
  onFilesSelected: (files: File[]) => void;
  filesToPreview: FileTypeWithMeta[];
  filesToUpload: File[];
};

export const useUploadMediaPreviewDefs = ({
  currentMediaType,
  onFilesSelected,
  filesToPreview,
  filesToUpload,
}: UseUploadMediaPreviewDefsParams) => {
  const selectedFileIds = useMemo(() => filesToUpload.map((file) => file.name), [filesToUpload]);
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();

  const toggleFileSelection = useCallback(
    (fileId: string) => {
      const isSelected = selectedFileIds.includes(fileId || '');
      let newSelectionIds = [];

      if (isSelected) {
        newSelectionIds = selectedFileIds.filter((id) => id !== fileId);
      } else {
        newSelectionIds = [...selectedFileIds, fileId];
      }

      const newFilesToUpload = filesToPreview.reduce((acc, file) => {
        if (newSelectionIds.includes(file.originalFile.name)) {
          acc.push(file.originalFile);
        }
        return acc;
      }, []);

      onFilesSelected(newFilesToUpload);
    },
    [selectedFileIds, filesToPreview, onFilesSelected]
  );

  return useMemo(() => {
    const columnDefs = [
      {
        field: 'fileThumbnailSrc',
        headerName: '',
        cellRendererParams: { currentMediaType },
        cellRenderer: ThumbnailCell,
        width: 104,
        cellClass: 'overflow-cell',
      },
      {
        field: 'fileName',
        headerName: t('common:form.label.name'),
        flex: 2,
        cellRenderer: NameCell,
      },
      {
        field: 'fileType',
        headerName: t('common:agGrid.format'),
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: FileTypeCell,
      },
      {
        // "actions' id is a key word for our mobile renderer
        colId: 'actions',
        field: 'id',
        cellRendererParams: { selectedFileIds, toggleFileSelection },
        headerName: '',
        width: 40,
        cellRenderer: CheckboxCell,
      },
    ];

    if (isMobile) {
      return [
        {
          field: 'id',
          headerName: t('common:profile.upload.mediaLabel', { count: filesToPreview?.length ?? 0 }),
          flex: 1,
          autoHeight: true,
          cellRendererParams: { columnDefs, selectedFileIds, toggleFileSelection, currentMediaType },
          cellRenderer: MobileCell,
        },
      ];
    }

    return columnDefs;
  }, [selectedFileIds, currentMediaType, filesToPreview, isMobile, toggleFileSelection, t]);
};
